/**
 * AuthProvider — authentication state machine for the Admin Panel shell.
 *
 *   restoring ──(cookie refresh failed → fresh visitor)──▶ unauthenticated
 *       │                                                       ▲
 *       └──(cookie refresh ok → /me)──▶ authenticated ── logout ┘
 *
 * Token custody is delegated to AuthSession (see session.ts for the full
 * §11.1 storage design — identity.yaml v1.1.0 ADMIN_WEB httpOnly-cookie flow,
 * adopted M2-20: the client never holds a refresh token, in mock OR real
 * mode, and session restore across a hard reload works in both). RBAC:
 * `hasPermission` drives hiding of unauthorized actions — the server remains
 * the authority.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createMockTransport } from "../api/mock/createMockTransport";
import { apiBaseUrl, createFetchTransport, type Transport } from "../api/transport";
import type { Me } from "./contract";
import { getMe, login as apiLogin, logout as apiLogout } from "./identityApi";
import { AuthSession } from "./session";

export type AuthStatus = "restoring" | "unauthenticated" | "authenticated";

/** Why the last session ended — drives the notice on the login screen. */
export type SessionEndReason = "expired" | "manual" | null;

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: Me | null;
  readonly sessionEndReason: SessionEndReason;
  readonly login: (phone: string, password: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly hasPermission: (permissionKey: string) => boolean;
  /** Transport for authenticated feature calls (admin screens plug in here). */
  readonly transport: Transport;
  readonly getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

function defaultTransport(): Transport {
  const base = apiBaseUrl();
  // Mock mode (§16.2): identity + geo + merchants served from one mock,
  // emulating the v1.1 ADMIN_WEB cookie inside the mock module.
  if (base === null) {
    // E2E seam (mock only): a restricted role's permission set.
    const scoped = import.meta.env.VITE_MOCK_PERMISSIONS;
    if (typeof scoped === "string" && scoped.trim() !== "") {
      return createMockTransport({ permissions: scoped.split(",").map((p) => p.trim()).filter(Boolean) });
    }
    return createMockTransport();
  }
  // Real mode: the httpOnly refresh cookie flows via credentials:"include".
  return createFetchTransport(base);
}

export interface AuthProviderProps {
  readonly children: ReactNode;
  /** Test seam — production uses env-driven wiring. */
  readonly transport?: Transport;
}

export function AuthProvider({ children, transport }: AuthProviderProps) {
  const transportRef = useRef<Transport | null>(null);
  transportRef.current ??= transport ?? defaultTransport();
  const activeTransport = transportRef.current;

  const [status, setStatus] = useState<AuthStatus>("restoring");
  const [user, setUser] = useState<Me | null>(null);
  const [sessionEndReason, setSessionEndReason] = useState<SessionEndReason>(null);

  const sessionRef = useRef<AuthSession | null>(null);
  sessionRef.current ??= new AuthSession(activeTransport, () => {
    setUser(null);
    setSessionEndReason("expired");
    setStatus("unauthenticated");
  });
  const session = sessionRef.current;

  // Session restore — runs once on mount (§14.4 shell requirement).
  // v1.1: the httpOnly cookie is invisible to script, so we always attempt
  // ONE silent refresh; a fresh visitor just falls through to /login.
  useEffect(() => {
    const lifecycle = { cancelled: false };
    // Function-call reads on purpose: control-flow narrowing must not assume
    // the flag is still false after an await (cleanup flips it concurrently).
    const isCancelled = (): boolean => lifecycle.cancelled;
    async function restore(): Promise<void> {
      const ok = await session.tryRestore();
      if (isCancelled()) return;
      if (!ok) {
        setStatus("unauthenticated");
        return;
      }
      const access = session.getAccessToken();
      if (access === null) {
        setStatus("unauthenticated");
        return;
      }
      try {
        const me = await getMe(activeTransport, access);
        if (isCancelled()) return;
        setUser(me);
        setSessionEndReason(null);
        setStatus("authenticated");
      } catch {
        if (isCancelled()) return;
        session.destroy();
        setStatus("unauthenticated");
      }
    }
    void restore();
    return () => {
      lifecycle.cancelled = true;
    };
  }, [session, activeTransport]);

  const login = useCallback(
    async (phone: string, password: string): Promise<void> => {
      const result = await apiLogin(activeTransport, { phone, password });
      session.adopt(result.tokens);
      const access = session.getAccessToken();
      let me: Me;
      if (access !== null) {
        try {
          me = await getMe(activeTransport, access);
        } catch {
          // /me hiccup must not block login — roles arrive on next restore.
          me = { ...result.user, roles: [], permissions: [] };
        }
      } else {
        me = { ...result.user, roles: [], permissions: [] };
      }
      setUser(me);
      setSessionEndReason(null);
      setStatus("authenticated");
    },
    [session, activeTransport],
  );

  const logout = useCallback(async (): Promise<void> => {
    const access = session.getAccessToken();
    try {
      if (access !== null) await apiLogout(activeTransport, access);
    } catch {
      // Server-side revocation failure never traps the user in the panel.
    } finally {
      session.destroy();
      setUser(null);
      setSessionEndReason("manual");
      setStatus("unauthenticated");
    }
  }, [session, activeTransport]);

  const hasPermission = useCallback(
    (permissionKey: string) => user?.permissions.includes(permissionKey) ?? false,
    [user],
  );

  const getAccessToken = useCallback(() => session.getAccessToken(), [session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      sessionEndReason,
      login,
      logout,
      hasPermission,
      transport: activeTransport,
      getAccessToken,
    }),
    [status, user, sessionEndReason, login, logout, hasPermission, activeTransport, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
